import React, { useState, useEffect } from 'react';
import { MeasurementScale, HomogeneousArray } from '../../types/scale.types';
import { ColumnSchema } from '../../types/core.types';
import { ContextHelpOverlay } from './ContextHelp/ContextHelpOverlay';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';
import logger from '../utils/logger';
import * as styles from '../styles/components/scale-edit-modal.css.ts';

interface ScaleEditModalProps {
  column: ColumnSchema;
  onSave: (scale: MeasurementScale, ordinalOrder?: HomogeneousArray, validCategories?: HomogeneousArray) => void;
  onCancel: () => void;
}

export const ScaleEditModal: React.FC<ScaleEditModalProps> = ({ column, onSave, onCancel }) => {
  const [scale, setScale] = useState<MeasurementScale>(column.scale?.scale || 'nominal');
  const [validCategories, setValidCategories] = useState<string[]>([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    // 既存の値を設定
    if (column.scale) {
      setScale(column.scale.scale);
      
      // ordinalOrderがある場合はそれを使用、なければvalidCategoriesを使用
      if (column.scale.ordinalOrder) {
        setValidCategories(column.scale.ordinalOrder.map(v => String(v)));
      } else if (column.scale.validCategories) {
        setValidCategories(column.scale.validCategories.map(v => String(v)));
      }
    }
  }, [column]);

  const handleAddCategory = () => {
    const trimmed = categoryInput.trim();
    if (trimmed && !validCategories.includes(trimmed)) {
      setValidCategories([...validCategories, trimmed]);
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (category: string) => {
    setValidCategories(validCategories.filter(c => c !== category));
  };

  const handleMoveCategory = (index: number, direction: 'up' | 'down') => {
    const newCategories = [...validCategories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newCategories.length) {
      [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
      setValidCategories(newCategories);
    }
  };

  const handleSave = () => {
    logger.info('[ScaleEditModal] Saving scale changes', {
      columnName: column.name,
      scale,
      validCategoriesCount: validCategories.length
    });

    // 尺度タイプに応じて適切なデータを送信
    let finalValidCategories: HomogeneousArray | undefined;
    let finalOrdinalOrder: HomogeneousArray | undefined;

    if (scale === 'nominal' && validCategories.length > 0) {
      finalValidCategories = validCategories;
    }

    if (scale === 'ordinal' && validCategories.length > 0) {
      // Ordinalの場合、validCategoriesの順序がそのままordinalOrderになる
      finalOrdinalOrder = validCategories;
      finalValidCategories = validCategories;
    }

    onSave(scale, finalOrdinalOrder, finalValidCategories);
  };

  return (
    <>
      <Dialog
        isOpen={true}
        onClose={onCancel}
        title="Edit Scale of Measurement"
        width="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <div>
          <div className={styles.columnInfo}>
            <span className={styles.columnName}>{column.name}</span>
            <span className={styles.columnType}>({column.type})</span>
          </div>

          <div className={styles.scaleTypeSection}>
            <label>Scale Type:</label>
            <select value={scale} onChange={(e) => setScale(e.target.value as MeasurementScale)}>
              <option value="nominal">Nominal (Categories)</option>
              <option value="ordinal">Ordinal (Ordered Categories)</option>
              <option value="interval">Interval (Differences meaningful)</option>
              <option value="ratio">Ratio (Zero is absolute)</option>
            </select>
          </div>

          {/* Nominal または Ordinal の場合、カテゴリ管理UI表示 */}
          {(scale === 'nominal' || scale === 'ordinal') && (
            <div className={styles.categoriesSection}>
              <h3>
                {scale === 'ordinal' ? 'Categories (in order from lowest to highest)' : 'Valid Categories'}
                <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px', fontWeight: 'normal' }}>
                  (Optional)
                </span>
                <button
                  className={styles.helpButton}
                  onClick={() => setShowHelp(true)}
                  title="Click for help"
                  style={{
                    marginLeft: '8px',
                    background: 'none',
                    border: '1px solid #666',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#666',
                    padding: 0
                  }}
                >
                  ?
                </button>
              </h3>
              <div className={styles.categoryInput}>
                <input
                  type="text"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  placeholder="Enter category name"
                />
                <button onClick={handleAddCategory}>Add</button>
              </div>
              
              <div className={styles.categoryList}>
                {validCategories.map((category, index) => (
                  <div key={category} className={styles.categoryItem}>
                    {scale === 'ordinal' && (
                      <span className={styles.orderIndex}>{index + 1}.</span>
                    )}
                    <span className={styles.categoryName}>{category}</span>
                    <div className={styles.categoryActions}>
                      {scale === 'ordinal' && (
                        <>
                          <button 
                            className={styles.moveButton}
                            onClick={() => handleMoveCategory(index, 'up')}
                            disabled={index === 0}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button 
                            className={styles.moveButton}
                            onClick={() => handleMoveCategory(index, 'down')}
                            disabled={index === validCategories.length - 1}
                            title="Move down"
                          >
                            ↓
                          </button>
                        </>
                      )}
                      <button 
                        className={styles.removeButton}
                        onClick={() => handleRemoveCategory(category)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* Interval/Ratio の場合の説明 */}
          {(scale === 'interval' || scale === 'ratio') && (
            <div className={styles.infoSection}>
              <p>
                {scale === 'interval' 
                  ? 'Interval scale: Differences between values are meaningful, but there is no true zero point (e.g., temperature in Celsius).'
                  : 'Ratio scale: Has a true zero point and ratios between values are meaningful (e.g., height, weight, count).'}
              </p>
            </div>
          )}
        </div>
      </Dialog>

      {/* Help Overlay using ContextHelpOverlay */}
      {showHelp && (
        <ContextHelpOverlay
          helpContent={
            <div>
              <h3>About {scale === 'ordinal' ? 'Ordinal' : 'Nominal'} Scale Categories</h3>

              {scale === 'ordinal' ? (
                <>
                  <p style={{ marginBottom: '16px' }}>
                    Ordinal scale represents ordered categorical data where the order matters but the differences between values may not be uniform.
                  </p>

                  <h4>When you specify categories:</h4>
                  <ul>
                    <li>Only the values you list will be considered valid</li>
                    <li>The order you specify will be used for sorting and analysis</li>
                    <li>Any values not in your list will be treated as invalid/missing</li>
                  </ul>

                  <h4 style={{ marginTop: '16px' }}>When you DON'T specify categories (recommended for most cases):</h4>
                  <ul>
                    <li>All unique values in the data will be considered valid</li>
                    <li>Values will be ordered using their natural ordering:
                      <ul style={{ marginTop: '8px' }}>
                        <li><strong>Numbers (int64/float64):</strong> Numerical order (1, 2, 3, 10, 20...)</li>
                        <li><strong>Text (string):</strong> Alphabetical order (A, B, C...)</li>
                        <li><strong>Dates:</strong> Chronological order (earliest to latest)</li>
                      </ul>
                    </li>
                  </ul>

                  <h4 style={{ marginTop: '16px' }}>Examples:</h4>
                  <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                    <strong>Example 1: Education Level</strong><br />
                    Categories: Elementary, High School, Bachelor's, Master's, PhD<br />
                    <em>Order matters, but the "distance" between levels varies</em>
                  </div>
                  <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                    <strong>Example 2: Survey Ratings</strong><br />
                    Categories: Strongly Disagree, Disagree, Neutral, Agree, Strongly Agree<br />
                    <em>Clear order, but intervals may not be equal</em>
                  </div>

                  <p style={{ marginTop: '16px', color: '#666' }}>
                    <strong>Tip:</strong> Use the ↑↓ arrows to reorder categories after adding them to the list.
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: '16px' }}>
                    Nominal scale represents categorical data with no inherent order (e.g., colors, countries, product types).
                  </p>

                  <h4>When you specify categories:</h4>
                  <ul>
                    <li>Only the values you list will be considered valid</li>
                    <li>Any values not in your list will be treated as invalid/missing</li>
                    <li>Useful for data validation and ensuring consistency</li>
                  </ul>

                  <h4 style={{ marginTop: '16px' }}>When you DON'T specify categories (recommended for exploratory analysis):</h4>
                  <ul>
                    <li>All unique values in the data will be considered valid</li>
                    <li>No validation is performed</li>
                    <li>Good for discovering all possible values in your data</li>
                  </ul>

                  <h4 style={{ marginTop: '16px' }}>Examples:</h4>
                  <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                    <strong>Example 1: Product Categories</strong><br />
                    Categories: Electronics, Clothing, Food, Books<br />
                    <em>No inherent order between categories</em>
                  </div>
                  <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                    <strong>Example 2: Blood Types</strong><br />
                    Categories: A, B, AB, O<br />
                    <em>Distinct categories with no ranking</em>
                  </div>
                </>
              )}
            </div>
          }
          onClose={() => setShowHelp(false)}
        />
      )}
    </>
  );
};